import React, { useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { CheckCircle2, Circle, ListChecks, ChevronRight } from 'lucide-react';
import api from '../../../utils/api';
import { Field, Input, Select, Button, Banner, Meter, LoadingBlock, ErrorBlock, errMsg, fmtDate, todayIso } from '../kit';
import { LinkButton } from '../file/shared';
import { AgreementForm } from '../file/ServiceAgreementPanel';

// A participant's setup: everything claiming, invoices and SC hours need before the first claim.
// Time can be logged straight away; claims wait until every item is done. The file loads the
// checklist once (useLoad) and passes it to both the banner and this list, so they never disagree.

// 'Claim checks and NDIA file' → 'claim checks and NDIA file' (acronyms keep their capitals).
const lowerFirst = (s) => { const t = String(s || ''); return t.charAt(0).toLowerCase() + t.slice(1); };

// One line under the file header, on every tab, until setup is complete.
export function SetupBanner({ checklist, onOpen }) {
  const d = checklist && checklist.data;
  if (!d || d.complete) return null;
  const done = d.items.filter((i) => i.done).length;
  return (
    <button type="button" onClick={onOpen}
      className="mb-5 w-full flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-amber-50 ring-1 ring-inset ring-amber-200 px-4 py-2.5 text-left text-[13px] text-amber-900 hover:bg-amber-100/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
      <ListChecks size={16} className="text-amber-700 flex-shrink-0" aria-hidden />
      <span className="font-semibold">Setup {done} of {d.items.length}</span>
      <span>— claims are on hold until it’s done</span>
      <span className="sm:ml-auto inline-flex items-center gap-0.5 font-semibold text-indigo-700">Finish setup<ChevronRight size={15} aria-hidden /></span>
    </button>
  );
}

// The list itself, shown in a Sheet. Each missing item is fixed in place.
export default function SetupChecklist({ checklist, coordinationId, staff = [], onAddService, onChanged, canEdit = true }) {
  const [open, setOpen] = useState(null);
  if (!checklist) return null;
  if (checklist.error) return <ErrorBlock error={checklist.error} onRetry={checklist.reload} />;
  if (!checklist.data) return <LoadingBlock rows={5} />;
  const { items, complete } = checklist.data;
  const done = items.filter((i) => i.done).length;
  const refresh = (data) => { if (data) checklist.setData(data); else checklist.reload(); setOpen(null); onChanged?.(); };

  return (
    <div className="space-y-4">
      {complete
        ? <Banner tone="emerald" title="Setup is complete">Everything claiming needs is in place.</Banner>
        : (
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-[13px] font-semibold text-slate-800">{done} of {items.length} done</p>
              <p className="text-[12.5px] text-slate-500">Time can be logged now; claims wait for this.</p>
            </div>
            <Meter value={done} max={items.length} tone="amber" label="Setup progress" />
          </div>
        )}
      {!canEdit && !complete && (
        <Banner tone="blue">Someone with the Manage referrals or Admin permission needs to finish these.</Banner>
      )}
      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <li key={it.key} className="py-3">
            <div className="flex items-start gap-3">
              {it.done ? <CheckCircle2 size={18} className="mt-0.5 text-emerald-600 flex-shrink-0" aria-hidden />
                : <Circle size={18} className="mt-0.5 text-amber-500 flex-shrink-0" aria-hidden />}
              <div className="flex-1 min-w-0">
                <p className={clsx('text-[13.5px] font-semibold', it.done ? 'text-slate-700' : 'text-slate-900')}>
                  <span className="sr-only">{it.done ? 'Done: ' : 'To do: '}</span>{it.label}
                </p>
                <p className="text-[12.5px] text-slate-500">{it.detail}</p>
                {it.neededFor && <p className="text-[12px] text-slate-500">Needed for {lowerFirst(it.neededFor)}</p>}
              </div>
              {canEdit && (
                <Button size="sm" variant={it.done ? 'ghost' : 'soft'} aria-expanded={it.key === 'budget_line' ? undefined : open === it.key}
                  onClick={() => (it.key === 'budget_line' ? onAddService?.() : setOpen(open === it.key ? null : it.key))}>
                  {it.done ? 'Change' : 'Fix'}
                </Button>
              )}
            </div>
            {open === it.key && (
              <div className="mt-3 sm:ml-7 rounded-xl bg-slate-50 p-3">
                <FixForm item={it} coordinationId={coordinationId} staff={staff} onDone={refresh} onCancel={() => setOpen(null)} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FixForm({ item, coordinationId, staff, onDone, onCancel }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v && v.target ? v.target.value : v }));
  const putSetup = async (body) => {
    setBusy(true);
    try { const r = await api.put(`/sc/engagements/${coordinationId}/setup`, body); toast.success('Saved'); onDone(r.data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const actions = (onSave) => (
    <div className="flex justify-end gap-2 mt-3"><Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button><Button size="sm" loading={busy} onClick={onSave}>Save</Button></div>
  );
  switch (item.key) {
    case 'funding_type':
      return (<>
        <Field label="How is the plan managed?">
          <Select value={f.funding_source || ''} onChange={set('funding_source')} options={[
            { value: 'ndia', label: 'NDIA-managed', hint: 'Claimed from the NDIA.' },
            { value: 'plan_managed', label: 'Plan-managed', hint: 'Invoiced to the plan manager.' },
            { value: 'self_managed', label: 'Self-managed', hint: 'Invoiced to the participant or their nominee.' }]} />
        </Field>
        {actions(() => putSetup({ funding_source: f.funding_source }))}
      </>);
    case 'ndis_plan':
      return (<>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="NDIS number"><Input inputMode="numeric" maxLength={9} placeholder="43…" value={f.ndis_number || ''} onChange={set('ndis_number')} /></Field>
          <Field label="Plan start"><Input type="date" value={f.plan_start || ''} onChange={set('plan_start')} /></Field>
          <Field label="Plan end"><Input type="date" value={f.plan_end || ''} onChange={set('plan_end')} /></Field>
        </div>
        {actions(() => putSetup(Object.fromEntries(Object.entries(f).filter(([, v]) => v))))}
      </>);
    case 'service_agreement':
      return <AgreementFix item={item} coordinationId={coordinationId} onDone={onDone} onCancel={onCancel} />;
    case 'invoice_recipient':
      return (<>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name"><Input value={f.invoice_recipient_name || ''} onChange={set('invoice_recipient_name')} /></Field>
          <Field label="Email"><Input type="email" value={f.invoice_recipient_email || ''} onChange={set('invoice_recipient_email')} /></Field>
        </div>
        {actions(() => putSetup({ invoice_recipient_name: f.invoice_recipient_name || '', invoice_recipient_email: f.invoice_recipient_email || '' }))}
      </>);
    case 'coordinator':
      return (<>
        <Field label="Support coordinator">
          <Select value={f.coordinator_id || ''} onChange={set('coordinator_id')} options={staff.map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name}` }))} />
        </Field>
        {actions(() => putSetup({ coordinator_id: f.coordinator_id }))}
      </>);
    default:
      return null;
  }
}

const nextDay = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  return y && m && d ? new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10) : '';
};

// The service agreement item opens the same form as Plan & Budgets: the agreement it names (the
// current one, the one that ended, or the next one) to change or extend, or a new one.
function AgreementFix({ item, coordinationId, onDone, onCancel }) {
  const a = item.agreement && item.agreement.id ? item.agreement : null;
  const [adding, setAdding] = useState(!a);
  const today = todayIso();
  const what = !a ? '' : a.status === 'ended' ? `the agreement that ended on ${fmtDate(a.date_to)}`
    : a.status === 'future' ? `the agreement starting ${fmtDate(a.date_from)}` : 'the current agreement';
  // A new agreement after one that ended starts the next day (no gap), at the same rate.
  const defaults = a ? { date_from: a.status === 'ended' && a.date_to && a.date_to < today ? nextDay(a.date_to) : today, hourly_rate: a.hourly_rate } : { date_from: today };
  return (
    <div className="space-y-3">
      {a && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[12.5px] font-semibold text-slate-700">{adding ? 'New agreement' : `Change ${what}`}</p>
          <LinkButton onClick={() => setAdding(!adding)}>{adding ? `Change ${what} instead` : 'Add a new agreement instead'}</LinkButton>
        </div>
      )}
      <AgreementForm key={adding ? 'new' : a.id} coordinationId={coordinationId} agreement={adding ? null : a} defaults={defaults}
        onSaved={(data) => onDone(data && data.checklist)} onCancel={onCancel} />
    </div>
  );
}
